import React, { useEffect, useState, useRef } from "react"
import "./Levelupnotification.css"

/**
 * LevelUpNotification
 * ───────────────────────────────────────────────────────────────
 * Props:
 *   myScore  {Object|null}  — the current user's score object from the leaderboard
 *   show     {boolean}      — controlled visibility
 *   onClose  {Function}     — callback when user dismisses
 */

/* ── Exact same CornerSVG as Inventory.jsx ──────────────────── */
const CornerSVG = ({ gold = false }) => {
  const c1  = gold ? "#8a6018" : "rgba(59,191,255,.6)"
  const c2  = gold ? "#c9962a" : "rgba(59,191,255,.3)"
  const dot = gold ? "#f5b731" : "#3bbfff"
  const vn  = gold ? "#d4a830" : "rgba(59,191,255,.5)"

  const W = 90, H = 90
  const style = { position:"absolute", display:"block", pointerEvents:"none", overflow:"visible", width:W, height:H }

  /* ── Top-left: arms go RIGHT and DOWN ── */
  const TL = () => (
    <svg style={{...style, top:-5, left:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 86 L4 10 Q4 4 10 4 L86 4" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M9 80 L9 14 Q9 9 14 9 L80 9" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="8" cy="8" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="8" cy="8" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M16 4 Q22 10 16 17 Q23 13 30 17 Q25 10 30 4 Q23 8 16 4Z" fill={c1}/>
      <path d="M4 16 Q10 22 17 16 Q13 23 17 30 Q10 25 4 30 Q8 23 4 16Z" fill={c1}/>
      <circle cx="16" cy="16" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="16" cy="16" r="2.5" fill={dot}/>
      <circle cx="26" cy="8" r="3.2" fill={vn}/>
      <circle cx="21" cy="5" r="2" fill={dot} opacity=".85"/>
      <circle cx="8" cy="26" r="3.2" fill={vn}/>
      <circle cx="5" cy="21" r="2" fill={dot} opacity=".85"/>
      <path d="M8 38 Q11 30 16 22" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M38 8 Q30 11 22 16" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="10" cy="40" r="2" fill={dot} opacity=".5"/>
      <circle cx="40" cy="10" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  /* ── Top-right: arms go LEFT and DOWN — mirrored on X axis ── */
  const TR = () => (
    <svg style={{...style, top:-5, right:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M86 86 L86 10 Q86 4 80 4 L4 4" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M81 80 L81 14 Q81 9 76 9 L10 9" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="82" cy="8" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="82" cy="8" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M74 4 Q68 10 74 17 Q67 13 60 17 Q65 10 60 4 Q67 8 74 4Z" fill={c1}/>
      <path d="M86 16 Q80 22 73 16 Q77 23 73 30 Q80 25 86 30 Q82 23 86 16Z" fill={c1}/>
      <circle cx="74" cy="16" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="74" cy="16" r="2.5" fill={dot}/>
      <circle cx="64" cy="8" r="3.2" fill={vn}/>
      <circle cx="69" cy="5" r="2" fill={dot} opacity=".85"/>
      <circle cx="82" cy="26" r="3.2" fill={vn}/>
      <circle cx="85" cy="21" r="2" fill={dot} opacity=".85"/>
      <path d="M82 38 Q79 30 74 22" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M52 8 Q60 11 68 16" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="80" cy="40" r="2" fill={dot} opacity=".5"/>
      <circle cx="50" cy="10" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  /* ── Bottom-left: arms go RIGHT and UP — mirrored on Y axis ── */
  const BL = () => (
    <svg style={{...style, bottom:-5, left:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4 L4 80 Q4 86 10 86 L86 86" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M9 10 L9 76 Q9 81 14 81 L80 81" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="8" cy="82" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="8" cy="82" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M16 86 Q22 80 16 73 Q23 77 30 73 Q25 80 30 86 Q23 82 16 86Z" fill={c1}/>
      <path d="M4 74 Q10 68 17 74 Q13 67 17 60 Q10 65 4 60 Q8 67 4 74Z" fill={c1}/>
      <circle cx="16" cy="74" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="16" cy="74" r="2.5" fill={dot}/>
      <circle cx="26" cy="82" r="3.2" fill={vn}/>
      <circle cx="21" cy="85" r="2" fill={dot} opacity=".85"/>
      <circle cx="8" cy="64" r="3.2" fill={vn}/>
      <circle cx="5" cy="69" r="2" fill={dot} opacity=".85"/>
      <path d="M8 52 Q11 60 16 68" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M38 82 Q30 79 22 74" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="10" cy="50" r="2" fill={dot} opacity=".5"/>
      <circle cx="40" cy="80" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  /* ── Bottom-right: arms go LEFT and UP — mirrored on both axes ── */
  const BR = () => (
    <svg style={{...style, bottom:-5, right:-5}} viewBox="0 0 90 90" xmlns="http://www.w3.org/2000/svg">
      <path d="M86 4 L86 80 Q86 86 80 86 L4 86" fill="none" stroke={c1} strokeWidth="2.5"/>
      <path d="M81 10 L81 76 Q81 81 76 81 L10 81" fill="none" stroke={c2} strokeWidth="1.2"/>
      <circle cx="82" cy="82" r="4.5" fill={dot} stroke={c1} strokeWidth="1.2"/>
      <circle cx="82" cy="82" r="2" fill="#fff8c0" opacity=".7"/>
      <path d="M74 86 Q68 80 74 73 Q67 77 60 73 Q65 80 60 86 Q67 82 74 86Z" fill={c1}/>
      <path d="M86 74 Q80 68 73 74 Q77 67 73 60 Q80 65 86 60 Q82 67 86 74Z" fill={c1}/>
      <circle cx="74" cy="74" r="5" fill={c1} stroke={vn} strokeWidth="1.2"/>
      <circle cx="74" cy="74" r="2.5" fill={dot}/>
      <circle cx="64" cy="82" r="3.2" fill={vn}/>
      <circle cx="69" cy="85" r="2" fill={dot} opacity=".85"/>
      <circle cx="82" cy="64" r="3.2" fill={vn}/>
      <circle cx="85" cy="69" r="2" fill={dot} opacity=".85"/>
      <path d="M82 52 Q79 60 74 68" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <path d="M52 82 Q60 79 68 74" fill="none" stroke={c2} strokeWidth="1" strokeDasharray="3 2.5"/>
      <circle cx="80" cy="50" r="2" fill={dot} opacity=".5"/>
      <circle cx="50" cy="80" r="2" fill={dot} opacity=".5"/>
    </svg>
  )

  return (
    <div style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:10, overflow:"visible" }} aria-hidden="true">
      <TL/><TR/><BL/><BR/>
    </div>
  )
}

/* ── Alert circle-i icon ── */
const AlertIcon = () => (
  <svg
    width="24" height="24" viewBox="0 0 24 24"
    fill="none" stroke="#d4d4d4"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8"  x2="12" y2="12" />
    <circle cx="12" cy="16" r="0.5" fill="#d4d4d4" />
  </svg>
)

const LevelUpNotification = ({ myScore, show, onClose }) => {
  const [visible, setVisible] = useState(false)
  const [mode, setMode]       = useState("achievement")
  const cardRef               = useRef(null)

  useEffect(() => {
    if (!show) return
    const t = setTimeout(() => {
      setMode((myScore?.total ?? 0) > 0 ? "achievement" : "nudge")
      setVisible(true)
    }, 900)
    return () => clearTimeout(t)
  }, [show, myScore])

  const handleClose = () => {
    setVisible(false)
    setTimeout(() => onClose && onClose(), 350)
  }

  useEffect(() => {
    if (visible && cardRef.current) {
      cardRef.current.style.animation = "none"
      void cardRef.current.offsetHeight
      cardRef.current.style.animation = ""
    }
  }, [visible])

  if (!visible) return null

  const isAchievement = mode === "achievement"
  const score         = myScore?.total ?? 0
  const rank          = myScore?.rank  ?? null

  const achievementLines = []
  if (score > 0)
    achievementLines.push(score.toLocaleString() + " leaderboard points earned")
  if (rank === 1)       achievementLines.push("🥇 You're in 1st place!")
  else if (rank === 2)  achievementLines.push("🥈 You're in 2nd place!")
  else if (rank === 3)  achievementLines.push("🥉 You're in 3rd place!")
  else if (rank)        achievementLines.push("Ranked #" + rank + " overall")

  const nudgeLines = [
    "Attend sessions and check in",
    "Visit demo booths at the expo",
    "Participate in live polls & quizzes",
    "Earn points on the leaderboard",
  ]

  const lines = isAchievement ? achievementLines : nudgeLines

  return (
    <div
      className={"lun-overlay" + (visible ? " lun-overlay--active" : "")}
      onClick={e => { if (e.target === e.currentTarget) handleClose() }}
      role="dialog"
      aria-modal="true"
    >
      <div className="lun-card" ref={cardRef}>

        {/* Corners — same explicit 4-SVG approach as Inventory */}
        <CornerSVG gold />

        {/* Inner inset border */}
        <div className="lun-inner-border" />

        {/* Top rule */}
        <div className="lun-rule" />

        {/* Header */}
        <div className="lun-header">
          <AlertIcon />
          <span className="lun-alert-text">
            {isAchievement ? "ALERT" : "HEADS UP"}
          </span>
        </div>

        {/* Bottom rule */}
        <div className="lun-rule" />

        {/* Main message */}
        <div className={"lun-message" + (isAchievement ? "" : " lun-message--nudge")}>
          {isAchievement ? "YOU HAVE LEVELED UP!" : "YOU HAVEN'T LEVELED UP YET!"}
        </div>

        {/* Bullet list */}
        <ul className={"lun-list" + (isAchievement ? "" : " lun-list--nudge")}>
          {lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>

        {/* Dismiss */}
        <button className="lun-dismiss" onClick={handleClose}>
          {isAchievement ? "CONTINUE →" : "LET'S GO →"}
        </button>

      </div>
    </div>
  )
}

export default LevelUpNotification